package com.havn.app.core.settings

import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlin.math.roundToInt

enum class HavnMeasurementSystem(val code: String) {
    Metric("metric"),
    Imperial("imperial");

    companion object {
        fun fromCode(code: String?): HavnMeasurementSystem = entries.firstOrNull { it.code == code } ?: Metric
    }
}

enum class HavnCurrency(val code: String, val symbol: String, val displayName: String) {
    Eur("EUR", "€", "Euro"),
    Usd("USD", "$", "US dollar"),
    Gbp("GBP", "£", "British pound"),
    Chf("CHF", "CHF", "Swiss franc"),
    Czk("CZK", "Kč", "Czech koruna"),
    Pln("PLN", "zł", "Polish zloty"),
    Huf("HUF", "Ft", "Hungarian forint"),
    Ron("RON", "lei", "Romanian leu");

    companion object {
        fun fromCode(code: String?): HavnCurrency = entries.firstOrNull { it.code == code } ?: Eur
    }
}

data class HavnUnitPreferences(
    val measurementSystem: HavnMeasurementSystem = HavnMeasurementSystem.Metric,
    val currency: HavnCurrency = HavnCurrency.Eur
) {
    val areaLabel: String
        get() = when (measurementSystem) {
            HavnMeasurementSystem.Metric -> "m²"
            HavnMeasurementSystem.Imperial -> "sq ft"
        }
}

val LocalHavnUnitPreferences = staticCompositionLocalOf { HavnUnitPreferences() }

class HavnUnitPreferencesRepository(
    initialPreferences: HavnUnitPreferences = loadStoredHavnUnitPreferences()
) {
    private val _preferences = MutableStateFlow(initialPreferences)
    val preferences: StateFlow<HavnUnitPreferences> = _preferences.asStateFlow()

    fun setMeasurementSystem(system: HavnMeasurementSystem) {
        val next = _preferences.value.copy(measurementSystem = system)
        _preferences.value = next
        storeHavnUnitPreferences(next)
    }

    fun setCurrency(currency: HavnCurrency) {
        val next = _preferences.value.copy(currency = currency)
        _preferences.value = next
        storeHavnUnitPreferences(next)
    }
}

fun Long.formatCurrency(preferences: HavnUnitPreferences): String {
    if (this <= 0L) return "Price on request"
    val grouped = toString().reversed().chunked(3).joinToString(" ").reversed()
    return when (preferences.currency) {
        HavnCurrency.Eur -> "$grouped ${preferences.currency.symbol}"
        HavnCurrency.Usd -> "${preferences.currency.symbol}${grouped.replace(' ', ',')}"
        HavnCurrency.Gbp -> "${preferences.currency.symbol}${grouped.replace(' ', ',')}"
        HavnCurrency.Chf -> "${preferences.currency.symbol} $grouped"
        HavnCurrency.Czk -> "$grouped ${preferences.currency.symbol}"
        HavnCurrency.Pln -> "$grouped ${preferences.currency.symbol}"
        HavnCurrency.Huf -> "$grouped ${preferences.currency.symbol}"
        HavnCurrency.Ron -> "$grouped ${preferences.currency.symbol}"
    }
}

fun Int.toDisplayArea(preferences: HavnUnitPreferences): Int = when (preferences.measurementSystem) {
    HavnMeasurementSystem.Metric -> (this * 0.092903).roundToInt()
    HavnMeasurementSystem.Imperial -> this
}

fun Int.toStoredSquareFeet(preferences: HavnUnitPreferences): Int = when (preferences.measurementSystem) {
    HavnMeasurementSystem.Metric -> (this / 0.092903).roundToInt()
    HavnMeasurementSystem.Imperial -> this
}

fun Int.formatArea(preferences: HavnUnitPreferences): String =
    "${toDisplayArea(preferences)} ${preferences.areaLabel}"

expect fun loadStoredHavnUnitPreferences(): HavnUnitPreferences

expect fun storeHavnUnitPreferences(preferences: HavnUnitPreferences)
